import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, cents } from "../lib/api";
import { useCart } from "../context/CartContext";
import usePageMeta from "../hooks/usePageMeta";
import { track } from "../lib/analytics";

export default function OrderConfirmation() {
  usePageMeta("Order Confirmation | Tiff. Wellness Co.", "Your tiff. wellness co. order status.", { robots: "noindex,nofollow" });
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });
  const { clear } = useCart();
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ phase: "missing" }); return; }
    let stop = false;
    const poll = async () => {
      if (stop || tries.current > 10) { if (tries.current > 10) setState({ phase: "timeout" }); return; }
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState({ phase: "paid", data }); track("purchase", { transaction_id: sessionId }); clear(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState({ phase: "failed" }); return; }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="max-w-2xl mx-auto px-4 py-16">
      {state.phase === "checking" && <div className="text-center" data-testid="order-confirmation-checking"><h1 className="text-3xl mb-3">Confirming your payment…</h1><p className="text-sm text-[#171715]/70">We only confirm orders after Stripe verifies payment — one moment.</p></div>}
      {state.phase === "missing" && <div className="text-center"><h1 className="text-3xl">No checkout session found</h1><Link to="/shop" className="underline text-sm">Back to shop</Link></div>}
      {state.phase === "failed" && <div className="text-center" data-testid="order-confirmation-failed"><h1 className="text-3xl mb-3">Payment not completed</h1><p className="text-sm text-[#171715]/70 mb-4">Your card was not charged, or the session expired. Your held pickup slot has been released.</p><Link to="/cart" className="btn-ink">Return to cart</Link></div>}
      {state.phase === "timeout" && <div className="text-center"><h1 className="text-3xl mb-3">Still processing</h1><p className="text-sm text-[#171715]/70">We haven't received payment confirmation yet. Check your account shortly — we never mark an order paid until Stripe confirms.</p></div>}
      {state.phase === "paid" && (
        <div data-testid="order-confirmation-paid">
          <p className="eyebrow mb-2">Payment confirmed by Stripe (test mode)</p>
          <h1 className="text-4xl mb-6">Thank you — your juice is on the schedule.</h1>
          {state.data.subscription && (
            <div className="card-paper p-5 mb-4 text-sm" data-testid="confirmation-subscription-card">
              <p className="font-medium mb-2">Subscription active</p>
              <p>{state.data.subscription.plan} · {cents(state.data.subscription.price_cents)} per pickup</p>
              <p className="text-[#171715]/70 mt-1">Next charge: {new Date(state.data.subscription.next_charge_at).toLocaleDateString("en-US", { timeZone: "America/Chicago" })}</p>
              <p className="text-[#171715]/70">Manage, skip, pause or cancel anytime from your <Link to="/account" className="underline">account</Link>.</p>
            </div>
          )}
          {state.data.order && (
            <div className="card-paper p-5 text-sm" data-testid="confirmation-order-card">
              <p className="font-medium">Order {state.data.order.order_number}</p>
              <ul className="mt-2 text-[#171715]/80">{state.data.order.items.map((i) => <li key={i.slug}>{i.qty}× {i.name}</li>)}</ul>
              <p className="mt-2">Total: <span className="font-mono">{cents(state.data.order.total_cents)}</span> (pre-tax)</p>
              <p className="mt-2">{state.data.order.pickup.location_name} — {state.data.order.pickup.date} · {state.data.order.pickup.window_label}</p>
              {state.data.order.pickup_code && (
                <div className="mt-3 flex items-center gap-4">
                  <QRCodeSVG data-testid="confirmation-pickup-qr" value={state.data.order.pickup_code} size={88} bgColor="#ffffff" fgColor="#171715" aria-label={`Pickup QR code ${state.data.order.pickup_code}`} />
                  <p className="font-mono text-lg" data-testid="confirmation-pickup-code">Pickup code: {state.data.order.pickup_code}</p>
                </div>
              )}
              <p className="text-xs text-[#171715]/55 mt-2">Show the QR or code at the counter. It can only be redeemed once; the QR contains only the code — no personal data.</p>
            </div>
          )}
          <div className="mt-6 flex gap-3"><Link to="/account" className="btn-ink" data-testid="confirmation-account-link">Go to my account</Link><Link to="/shop" className="btn-ghost">Keep shopping</Link></div>
        </div>
      )}
    </div>
  );
}
